import { useMemo, useState } from "react";
import { apiClient } from "@/context/AuthContext";
import { Plus, X, AlertTriangle, Check, Building2, ShieldCheck, ClipboardCheck } from "lucide-react";

export default function NewAssessmentModal({ classes, projects, authorities = [], onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [projectId, setProjectId] = useState("");
  const [projectName, setProjectName] = useState("");
  const [projectLocation, setProjectLocation] = useState("İstanbul · Türkiye");

  const [buildingName, setBuildingName] = useState("");
  const [classId, setClassId] = useState(classes[0]?.id || "");
  const [hazardClass, setHazardClass] = useState("medium");
  const [floors, setFloors] = useState(3);
  const [grossArea, setGrossArea] = useState(1200);
  const [occupancy, setOccupancy] = useState(80);
  const [localAuthority, setLocalAuthority] = useState("none");

  const [title, setTitle] = useState("İlk ön değerlendirme");

  const selectedClass = useMemo(() => classes.find((c) => c.id === classId), [classes, classId]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      let realProjectId = projectId;
      if (!realProjectId) {
        const { data } = await apiClient.post("/projects", { name: projectName, location: projectLocation });
        realProjectId = data.id;
      }
      const { data: building } = await apiClient.post("/buildings", {
        project_id: realProjectId, name: buildingName, class_id: classId,
        hazard_class: hazardClass, floors: Number(floors) || 1,
        gross_area: Number(grossArea) || 0, occupancy: Number(occupancy) || 0,
        local_authority: localAuthority,
      });
      const { data: assessment } = await apiClient.post("/assessments", {
        building_id: building.id, title,
      });
      onCreated(assessment);
    } catch (err) {
      setError(err?.response?.data?.detail || "Kayıt oluşturulamadı");
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-backdrop" data-testid="new-assessment-modal">
      <form className="modal wide-modal" onSubmit={submit}>
        <div className="modal-head">
          <div><div className="eyebrow">YENİ ÇALIŞMA · {step}/3</div><h2>Değerlendirme oluştur</h2></div>
          <button type="button" className="icon-button" onClick={onClose} data-testid="close-modal-button"><X size={17}/></button>
        </div>
        <div className="stepper">
          {["Proje", "Bina", "Değerlendirme"].map((label, idx) => (
            <div key={label} className={`step ${step === idx+1 ? "active" : step > idx+1 ? "done" : ""}`}>
              <span>{idx+1}</span>{label}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="step-body">
            <label><Building2 size={14}/> Mevcut proje
              <select value={projectId} onChange={(e) => setProjectId(e.target.value)} data-testid="project-select">
                <option value="">Yeni proje oluştur</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            {!projectId && (
              <>
                <label>Proje adı
                  <input required value={projectName} onChange={(e) => setProjectName(e.target.value)} placeholder="Örn. Ankara depo binası" data-testid="project-name-input"/>
                </label>
                <label>Konum
                  <input value={projectLocation} onChange={(e) => setProjectLocation(e.target.value)} data-testid="project-location-input"/>
                </label>
              </>
            )}
            <div className="step-actions">
              <button type="button" className="secondary-btn" onClick={onClose}>İptal</button>
              <button type="button" className="primary-btn" onClick={() => setStep(2)} data-testid="step1-next-button" disabled={!projectId && !projectName.trim()}>Sonraki</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="step-body">
            <label>Bina adı
              <input required value={buildingName} onChange={(e) => setBuildingName(e.target.value)} placeholder="Örn. Ana depo blok A" data-testid="building-name-input"/>
            </label>
            <label><ClipboardCheck size={14}/> BYKHY kullanım sınıfı
              <select value={classId} onChange={(e) => setClassId(e.target.value)} data-testid="class-select">
                {classes.map((c) => <option key={c.id} value={c.id}>{`${c.name} · ${c.code}`}</option>)}
              </select>
            </label>
            {selectedClass && (
              <div className="class-detail" data-testid="class-detail">
                <small>{selectedClass.description}</small>
                <div className="class-chips">{selectedClass.subclasses.map((s) => <span key={s}>{s}</span>)}</div>
                <small><ShieldCheck size={13}/> Varsayılan tehlike sınıfı: <strong>{selectedClass.hazard_default}</strong> · {selectedClass.rule_packages.length} kural paketi uygulanacak</small>
              </div>
            )}
            <label>Tehlike sınıfı
              <select value={hazardClass} onChange={(e) => setHazardClass(e.target.value)} data-testid="hazard-select">
                <option value="low">Düşük</option>
                <option value="medium">Orta</option>
                <option value="high">Yüksek</option>
                <option value="very_high">Çok yüksek</option>
              </select>
            </label>
            <div className="row-fields">
              <label>Kat sayısı<input type="number" min={1} value={floors} onChange={(e) => setFloors(e.target.value)} data-testid="floors-input"/></label>
              <label>Brüt alan (m²)<input type="number" min={0} value={grossArea} onChange={(e) => setGrossArea(e.target.value)} data-testid="area-input"/></label>
              <label>Kullanıcı sayısı<input type="number" min={0} value={occupancy} onChange={(e) => setOccupancy(e.target.value)} data-testid="occupancy-input"/></label>
            </div>
            <label>Yerel kurum (opsiyonel)
              <select value={localAuthority} onChange={(e) => setLocalAuthority(e.target.value)} data-testid="authority-select">
                {authorities.map((a) => <option key={a.id} value={a.id}>{a.short} · {a.region}</option>)}
              </select>
            </label>
            <div className="step-actions">
              <button type="button" className="secondary-btn" onClick={() => setStep(1)}>Geri</button>
              <button type="button" className="primary-btn" onClick={() => setStep(3)} data-testid="step2-next-button" disabled={!buildingName.trim()}>Sonraki</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="step-body">
            <label>Değerlendirme başlığı
              <input required value={title} onChange={(e) => setTitle(e.target.value)} data-testid="assessment-title-input"/>
            </label>
            <div className="class-detail">
              <strong>{selectedClass?.name}</strong>
              <small>{selectedClass?.rule_packages.length} kural paketi · BYKHY {selectedClass?.code}</small>
              <small>Bulgular eksik veri olarak başlatılacak; her birini doğrulayarak tamamlayabilirsiniz.</small>
            </div>
            {error && <div className="auth-error" data-testid="create-error"><AlertTriangle size={14}/> {error}</div>}
            <div className="step-actions">
              <button type="button" className="secondary-btn" onClick={() => setStep(2)}>Geri</button>
              <button type="submit" className="primary-btn" disabled={loading} data-testid="create-assessment-submit">
                {loading ? "Oluşturuluyor…" : (<><Plus size={16}/> Değerlendirmeyi başlat</>)}
              </button>
            </div>
            <small className="legal-foot"><Check size={12}/> Bu çalışma resmi karar değildir; her bulgu kaynak maddeyle doğrulanmalıdır.</small>
          </div>
        )}
      </form>
    </div>
  );
}
